import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import IntlTelInput from 'react-intl-tel-input';
import 'react-intl-tel-input/dist/main.css';
import PropTypes from 'prop-types';
import {
  formatPhone,
  formatPlaceholder,
  getCountryData,
  getPreferredCountries,
} from '../../../../../utils/intlTel';
import { filterNonDigit } from '../utils';

// if onlyCountries and excludeCountries are both provided, onlyCountries will be used
const PhoneField = ({
  onCountryChange,
  defaultCountry,
  onlyCountries,
  excludeCountries,
  onPhoneNumberChange,
  e2eId = '',
  ...restProps
}) => {
  const [country, setCountry] = useState(defaultCountry?.toUpperCase());
  const phoneRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    if (defaultCountry) {
      setCountry(defaultCountry.toUpperCase());
    }
  }, [defaultCountry]);

  const handleCountryChange = useCallback(
    (currentNumber, selectedCountryData) => {
      onCountryChange(currentNumber, selectedCountryData);
      setCountry(selectedCountryData?.iso2?.toUpperCase());
    },
    [onCountryChange],
  );

  const customPlaceholder = useCallback(
    (value) => formatPlaceholder(value, country),
    [country],
  );

  const preferredCountries = useMemo(() => {
    const defaultPreferredCountries = getPreferredCountries(country);

    return onlyCountries
      ? defaultPreferredCountries.filter((each) =>
          onlyCountries.includes(each.toLowerCase()),
        )
      : defaultPreferredCountries;
  }, [onlyCountries, country]);

  useEffect(() => {
    const selectedFlagContainer = phoneRef.current.querySelector(
      '.intl-tel-input .selected-flag .iti-flag',
    );
    selectedFlagContainer.innerHTML = `<span class="country-code">${country}</span>`;
  }, [country]);

  const shouldUpdateCursorPosition = (
    unformattedPhoneLength,
    newPhoneLength,
  ) => {
    if (unformattedPhoneLength === newPhoneLength) {
      return false;
    }
    return inputRef.current?.tel?.selectionEnd === unformattedPhoneLength;
  };

  const handlePhoneNumberChange = (isValid, _newPhone, selectedCountryData) => {
    let newPhone = filterNonDigit(_newPhone);
    if (isValid) {
      const unformattedPhoneLength = newPhone.length;
      newPhone = formatPhone(newPhone, selectedCountryData.iso2);
      const formattedPhoneLength = newPhone.length;
      setTimeout(() => {
        if (
          shouldUpdateCursorPosition(
            unformattedPhoneLength,
            formattedPhoneLength,
          )
        ) {
          inputRef.current?.tel?.setSelectionRange(
            formattedPhoneLength,
            formattedPhoneLength,
          );
        }
      }, 0);
    }
    onPhoneNumberChange(isValid, newPhone, selectedCountryData);
  };

  return (
    <div ref={phoneRef} data-e2e-id={e2eId}>
      <IntlTelInput
        ref={inputRef}
        onSelectFlag={handleCountryChange}
        preferredCountries={preferredCountries}
        onlyCountries={onlyCountries}
        excludeCountries={excludeCountries}
        inputClassName="form-control"
        separateDialCode
        customPlaceholder={customPlaceholder}
        inputProps={{ type: 'tel' }}
        defaultCountry={country?.toLowerCase()}
        formatOnInit={false}
        onPhoneNumberChange={handlePhoneNumberChange}
        countriesData={getCountryData()}
        {...restProps}
      />
    </div>
  );
};

PhoneField.propTypes = {
  onCountryChange: PropTypes.func,
  onPhoneNumberChange: PropTypes.func,
  defaultCountry: PropTypes.string,
  onlyCountries: PropTypes.arrayOf(PropTypes.string),
  excludeCountries: PropTypes.arrayOf(PropTypes.string),
  e2eId: PropTypes.string,
};

export default PhoneField;
